// Organization Settings Page

import { useState, useEffect } from 'react';
import { useAuthStore, useHasRole } from '../../stores/auth-store';
import { trpc } from '../../lib/trpc';

interface TenantSettingsContainer {
  settings?: Record<string, unknown>;
}

function readNumericSetting(
  settings: Record<string, unknown> | undefined,
  key: 'sessionTimeoutMinutes' | 'sessionWarningMinutes',
): number | null {
  const value = settings?.[key];
  return typeof value === 'number' ? value : null;
}

const TIMEOUT_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
];

const WARNING_OPTIONS = [
  { value: 1, label: '1 minute' },
  { value: 2, label: '2 minutes' },
  { value: 3, label: '3 minutes' },
  { value: 5, label: '5 minutes' },
];

export function OrganizationPage() {
  const { user } = useAuthStore();
  const isAdmin = useHasRole('tenant_admin');

  const tenantQuery = trpc.tenant.getCurrent.useQuery();
  const updateMutation = trpc.tenant.updateSettings.useMutation({
    onSuccess: () => {
      tenantQuery.refetch();
    },
  });

  const settings = (tenantQuery.data as TenantSettingsContainer | undefined)?.settings;
  const currentTimeoutMinutes = readNumericSetting(settings, 'sessionTimeoutMinutes');
  const currentWarningMinutes = readNumericSetting(settings, 'sessionWarningMinutes') ?? 3;

  const [timeoutEnabled, setTimeoutEnabled] = useState(currentTimeoutMinutes !== null);
  const [timeoutMinutes, setTimeoutMinutes] = useState(currentTimeoutMinutes ?? 480);
  const [warningMinutes, setWarningMinutes] = useState(currentWarningMinutes);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync state when data loads
  useEffect(() => {
    if (settings) {
      const timeout = readNumericSetting(settings, 'sessionTimeoutMinutes');
      const warning = readNumericSetting(settings, 'sessionWarningMinutes') ?? 3;
      setTimeoutEnabled(timeout !== null);
      setTimeoutMinutes(timeout ?? 480);
      setWarningMinutes(warning);
      setHasChanges(false);
    }
  }, [settings]);

  const handleSaveSessionSettings = () => {
    updateMutation.mutate({
      sessionTimeoutMinutes: timeoutEnabled ? timeoutMinutes : null,
      sessionWarningMinutes: warningMinutes,
    });
    setHasChanges(false);
  };

  const handleTimeoutToggle = (enabled: boolean) => {
    setTimeoutEnabled(enabled);
    setHasChanges(true);
  };

  const handleTimeoutChange = (value: number) => {
    setTimeoutMinutes(value);
    setHasChanges(true);
  };

  const handleWarningChange = (value: number) => {
    setWarningMinutes(value);
    setHasChanges(true);
  };

  return (
    <div className="space-y-8">
      {/* Organization Info */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Name</div>
              <div className="font-medium text-gray-900">{user?.tenantName}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Slug</div>
              <div className="font-medium text-gray-900 font-mono text-sm">
                {user?.tenantSlug}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Free Trial</div>
              <p className="text-sm text-gray-500">
                You're currently on a free trial
              </p>
            </div>
            <button className="btn-primary" disabled>
              Upgrade
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Billing and subscription management coming soon.
        </p>
      </section>

      {/* Security Settings */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Security</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">Require 2FA for all users</div>
                <p className="text-sm text-gray-500">
                  Enforce two-factor authentication for all organization members
                </p>
              </div>
              <div className="text-sm text-gray-500">Coming soon</div>
            </div>
          </div>

          {/* Session Timeout - only visible to tenant_admin */}
          {isAdmin && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-medium text-gray-900">Session timeout</div>
                  <p className="text-sm text-gray-500">
                    Automatically log out inactive users after a set period
                  </p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={timeoutEnabled}
                    onChange={(e) => handleTimeoutToggle(e.target.checked)}
                  />
                  <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300"></div>
                </label>
              </div>

              {timeoutEnabled && (
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <div>
                    <label htmlFor="session-timeout" className="block text-sm font-medium text-gray-700 mb-1">
                      Timeout after inactivity
                    </label>
                    <select
                      id="session-timeout"
                      className="input w-full max-w-xs"
                      value={timeoutMinutes}
                      onChange={(e) => handleTimeoutChange(Number(e.target.value))}
                    >
                      {TIMEOUT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="session-warning" className="block text-sm font-medium text-gray-700 mb-1">
                      Warning before logout
                    </label>
                    <select
                      id="session-warning"
                      className="input w-full max-w-xs"
                      value={warningMinutes}
                      onChange={(e) => handleWarningChange(Number(e.target.value))}
                    >
                      {WARNING_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {hasChanges && (
                    <div className="flex items-center gap-3 pt-2">
                      <button
                        type="button"
                        onClick={handleSaveSessionSettings}
                        disabled={updateMutation.isPending}
                        className="btn-primary text-sm"
                      >
                        {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </button>
                      {updateMutation.isSuccess && (
                        <span className="text-sm text-green-600">Saved</span>
                      )}
                      {updateMutation.isError && (
                        <span className="text-sm text-red-600">
                          {updateMutation.error.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!timeoutEnabled && hasChanges && (
                <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={handleSaveSessionSettings}
                    disabled={updateMutation.isPending}
                    className="btn-primary text-sm"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Non-admin view */}
          {!isAdmin && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Session timeout</div>
                  <p className="text-sm text-gray-500">
                    {currentTimeoutMinutes !== null
                      ? `Sessions expire after ${currentTimeoutMinutes} minutes of inactivity`
                      : 'No session timeout configured'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Danger Zone */}
      <section>
        <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>
        <div className="border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">Delete Organization</div>
              <p className="text-sm text-gray-500">
                Permanently delete this organization and all its data
              </p>
            </div>
            <button
              className="btn-secondary text-red-600 border-red-200 hover:bg-red-50"
              disabled
            >
              Delete Organization
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
